import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Stack,
  Alert,
  Typography,
  InputAdornment,
} from "@mui/material";
import { MdPerson, MdPhone, MdTwoWheeler, MdBadge, MdSave } from "react-icons/md";

import { Surface, SectionTitle } from "components/ui";
import { T } from "theme/tokens";
import * as api from "../api";

/**
 * The fields an admin may change on a delivery partner.
 *
 * These four mirror the backend's allow-list exactly. Everything else on the
 * document — the OTP, the activation code, bank details, the wallet balance,
 * the verification flag — is rejected server-side, and offering an input for
 * a field that silently will not save is worse than not offering it.
 */
const FIELDS = [
  { key: "fullname", label: "Full name", icon: <MdPerson size={18} /> },
  { key: "phone", label: "Phone (login)", icon: <MdPhone size={18} /> },
  { key: "vehicleNumber", label: "Vehicle number", icon: <MdTwoWheeler size={18} /> },
  { key: "drivingLicense", label: "Driving licence", icon: <MdBadge size={18} /> },
];

export default function ProfileTab({ partnerId, partner, onChanged, notify }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setValues(
      FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: partner?.[f.key] ?? "" }), {})
    );
    setError("");
  }, [partner]);

  const changed = FIELDS.filter(
    (f) => String(values[f.key] ?? "") !== String(partner?.[f.key] ?? "")
  );
  const phoneChanged = changed.some((f) => f.key === "phone");

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      // Only what actually changed, so the audit timeline reads as the edits
      // someone made rather than one row per field on the form.
      const body = changed.reduce((acc, f) => ({ ...acc, [f.key]: values[f.key] }), {});
      await api.updateProfile(partnerId, body);
      notify?.(`${changed.length} field(s) updated.`);
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Could not save those changes."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Surface sx={{ p: { xs: 2, md: 2.5 } }}>
      <SectionTitle>Editable details</SectionTitle>

      <Grid container spacing={2} sx={{ mt: 0.2 }}>
        {FIELDS.map((field) => (
          <Grid item xs={12} sm={6} key={field.key}>
            <TextField
              fullWidth
              size="small"
              label={field.label}
              value={values[field.key] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{field.icon}</InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
        ))}
      </Grid>

      <Stack spacing={2} sx={{ mt: 2.5 }}>
        {/* Not a validation error — a warning about a consequence that is not
            obvious from a text field labelled "Phone". */}
        {phoneChanged && (
          <Alert severity="warning" sx={{ borderRadius: 2, fontSize: "0.83rem" }}>
            The phone number is this partner's login. After saving, they can only
            sign in with the new number — and it will be refused if another
            partner already uses it.
          </Alert>
        )}

        <Alert severity="info" sx={{ borderRadius: 2, fontSize: "0.83rem" }}>
          Bank details live on the Bank tab, and the wallet balance only moves
          through a payout. Neither can be set from here.
        </Alert>

        {error && <Alert severity="error">{error}</Alert>}

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography sx={{ fontSize: "0.8rem", color: T.subtle, mr: "auto" }}>
            {changed.length
              ? `${changed.length} unsaved change(s): ${changed
                  .map((f) => f.label)
                  .join(", ")}`
              : "Nothing changed yet"}
          </Typography>
          <Button
            variant="contained"
            startIcon={<MdSave />}
            onClick={save}
            disabled={saving || !changed.length}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              bgcolor: T.primary,
              "&:hover": { bgcolor: "#013838" },
            }}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </Stack>
      </Stack>
    </Surface>
  );
}
